import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../supabase';
import { REALMS } from '../../../../shared/src/realms';

// ── Types ──────────────────────────────────────────────
interface DifficultyCount { easy: number; medium: number; hard: number; }
export interface RealmStats {
  total: number;
  subTopics: Record<string, number>;
  difficulties: DifficultyCount;
  subTopicDiffs: Record<string, DifficultyCount>;
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface AdminContextValue {
  realmStats: Record<string, RealmStats>;
  totalCount: number;
  reviewCount: number;
  refreshStats: () => Promise<void>;
  isRefreshing: boolean;
  toasts: Toast[];
  toast: (message: string, type?: Toast['type']) => void;
  dismissToast: (id: number) => void;
}

const AdminCtx = createContext<AdminContextValue | null>(null);

// ── Provider ───────────────────────────────────────────
export function AdminProvider({ children }: { children: React.ReactNode }) {
  const [realmStats, setRealmStats] = useState<Record<string, RealmStats>>({});
  const [totalCount, setTotalCount] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastId = useRef(0);

  const refreshStats = useCallback(async () => {
    setIsRefreshing(true);
    try {
      // Paginated fetch to bypass 1000-row limit
      const pageSize = 1000;
      let allRows: { realm: string; sub_topic: string | null; difficulty: string; reviewed: boolean }[] = [];
      let page = 0;
      let hasMore = true;

      while (hasMore) {
        const { data } = await supabase
          .from('questions')
          .select('realm, sub_topic, difficulty, reviewed')
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (data && data.length > 0) {
          allRows = allRows.concat(data);
          hasMore = data.length === pageSize;
          page++;
        } else {
          hasMore = false;
        }
      }

      setTotalCount(allRows.length);

      let unreviewed = 0;
      const emptyDiff = (): DifficultyCount => ({ easy: 0, medium: 0, hard: 0 });
      const stats: Record<string, RealmStats> = {};

      allRows.forEach(q => {
        if (!stats[q.realm]) stats[q.realm] = { total: 0, subTopics: {}, difficulties: emptyDiff(), subTopicDiffs: {} };
        stats[q.realm].total++;
        const d = (q.difficulty || 'medium') as keyof DifficultyCount;
        if (stats[q.realm].difficulties[d] !== undefined) stats[q.realm].difficulties[d]++;
        const st = q.sub_topic || 'Uncategorized';
        stats[q.realm].subTopics[st] = (stats[q.realm].subTopics[st] || 0) + 1;
        if (!stats[q.realm].subTopicDiffs[st]) stats[q.realm].subTopicDiffs[st] = emptyDiff();
        if (stats[q.realm].subTopicDiffs[st][d] !== undefined) stats[q.realm].subTopicDiffs[st][d]++;
        if (!q.reviewed) unreviewed++;
      });

      setRealmStats(stats);
      setReviewCount(unreviewed);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => { refreshStats(); }, [refreshStats]);

  const toast = useCallback((message: string, type: Toast['type'] = 'info') => {
    const id = ++toastId.current;
    setToasts(prev => [...prev, { id, message, type }]);
    // Auto-dismiss (errors stay longer)
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, type === 'error' ? 8000 : 4000);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <AdminCtx.Provider value={{ realmStats, totalCount, reviewCount, refreshStats, isRefreshing, toasts, toast, dismissToast }}>
      {children}
      {/* Toast container */}
      {toasts.length > 0 && (
        <div className="admin-toast-container">
          {toasts.map(t => (
            <div key={t.id} className={`admin-toast admin-toast-${t.type}`} onClick={() => dismissToast(t.id)}>
              {t.type === 'success' && '✓ '}{t.type === 'error' && '✗ '}{t.message}
            </div>
          ))}
        </div>
      )}
    </AdminCtx.Provider>
  );
}

// ── Hook ───────────────────────────────────────────────
export function useAdmin() {
  const ctx = useContext(AdminCtx);
  if (!ctx) throw new Error('useAdmin must be used within AdminProvider');
  return ctx;
}
